import type { SVGProps } from 'react';

/**
 * Inline SVG icon set — paths lifted verbatim from `Guruji ERP.dc.html`.
 *
 * No icon library: the design draws its own 24×24 stroke paths, and matching
 * them exactly matters more than the convenience of a package. The nav renders
 * icons by NAME from the module registry, so the registry stays a plain data
 * file and a typo there is a compile error.
 *
 * Multi-subpath icons are stored as an array so each `<path>` keeps its own
 * attributes where the design gave it some (dashes, separate rects).
 */

export type IconName =
  | 'dashboard'
  | 'leads'
  | 'customers'
  | 'tours'
  | 'bookings'
  | 'invoices'
  | 'vouchers'
  | 'credit'
  | 'payments'
  | 'vendors'
  | 'reports'
  | 'settings'
  | 'vehicles'
  | 'expenses'
  | 'portal'
  | 'website'
  | 'bell'
  | 'search'
  | 'plus'
  | 'menu'
  | 'chevron-left'
  | 'chevron-right'
  | 'chevron-down'
  | 'arrow-right'
  | 'arrow-up-right'
  | 'check'
  | 'alert'
  | 'clock'
  | 'star'
  | 'close'
  | 'logout';

interface IconShape {
  /** `d` attributes rendered as <path>. */
  paths: readonly string[];
  /** Extra primitives the design used instead of paths. */
  circles?: ReadonlyArray<{ cx: number; cy: number; r: number }>;
  rects?: ReadonlyArray<{ x: number; y: number; width: number; height: number; rx?: number }>;
  /** Default stroke-width when the design deviated from 2. */
  strokeWidth?: number;
}

const ICONS: Readonly<Record<IconName, IconShape>> = Object.freeze({
  dashboard: {
    paths: [],
    rects: [
      { x: 3, y: 3, width: 7, height: 9 },
      { x: 14, y: 3, width: 7, height: 5 },
      { x: 14, y: 12, width: 7, height: 9 },
      { x: 3, y: 16, width: 7, height: 5 },
    ],
  },
  leads: {
    paths: ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M22 11h-6'],
    circles: [{ cx: 9, cy: 7, r: 4 }],
  },
  customers: {
    paths: [
      'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2',
      'M23 21v-2a4 4 0 0 0-3-3.87',
      'M16 3.13a4 4 0 0 1 0 7.75',
    ],
    circles: [{ cx: 9, cy: 7, r: 4 }],
  },
  tours: {
    paths: [
      'M17.8 19.2 16 11l3.5-3.5a2.5 2.5 0 0 0-3.5-3.5L12.5 7.5 4.3 5.7a1 1 0 0 0-1 1.6L8 11l-2 4-2.5.5a1 1 0 0 0-.4 1.7l2.9 2.9a1 1 0 0 0 1.7-.4L8 17l4-2 3.7 4.7a1 1 0 0 0 1.6-.3z',
    ],
  },
  bookings: {
    paths: ['M8 2v4M16 2v4M3 10h18'],
    rects: [{ x: 3, y: 4, width: 18, height: 17, rx: 2 }],
  },
  invoices: {
    paths: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6M9 13h6M9 17h4'],
  },
  vouchers: {
    paths: [
      'M2 9V7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v2a3 3 0 0 0 0 6v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-2a3 3 0 0 0 0-6z',
      'M13 5v14',
    ],
  },
  credit: {
    paths: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6M9 15h6'],
  },
  payments: {
    paths: ['M2 10h20'],
    rects: [{ x: 2, y: 5, width: 20, height: 14, rx: 2 }],
  },
  vendors: {
    paths: ['M3 21V7l9-4 9 4v14', 'M3 21h18M9 21v-6h6v6M7 11h.01M12 11h.01M17 11h.01'],
  },
  reports: {
    paths: ['M3 3v18h18', 'M7 15l4-5 3 3 5-7'],
  },
  settings: {
    paths: [
      'M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z',
    ],
    circles: [{ cx: 12, cy: 12, r: 3 }],
  },
  // Not in the design's sidebar — added so the seeded VEHICLE_MANAGEMENT and
  // EXPENSE_TRACKING permissions are reachable. Drawn in the same stroke style.
  vehicles: {
    paths: ['M3 6a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v10H3zM14 9h4l3 3v4h-7'],
    circles: [
      { cx: 7, cy: 17, r: 2 },
      { cx: 18, cy: 17, r: 2 },
    ],
  },
  expenses: {
    paths: ['M5 2h14v20l-3-2-2 2-2-2-2 2-2-2-3 2zM9 8h6M9 12h6'],
  },
  portal: {
    paths: ['M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18'],
    circles: [{ cx: 12, cy: 12, r: 9 }],
  },
  // New for the public-website module (not the Customer Portal above — a
  // different, still-unbuilt feature). Same globe construction, its own
  // name, so the two can never be confused if Customer Portal ever ships.
  website: {
    paths: ['M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18'],
    circles: [{ cx: 12, cy: 12, r: 9 }],
  },
  bell: {
    paths: ['M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9', 'M13.7 21a2 2 0 0 1-3.4 0'],
  },
  search: {
    paths: ['m20 20-3.5-3.5'],
    circles: [{ cx: 11, cy: 11, r: 7 }],
  },
  plus: { paths: ['M12 5v14M5 12h14'], strokeWidth: 2.4 },
  menu: { paths: ['M3 5h18M3 12h18M3 19h18'], strokeWidth: 2.2 },
  'chevron-left': { paths: ['m15 6-6 6 6 6'], strokeWidth: 2.4 },
  'chevron-right': { paths: ['m9 6 6 6-6 6'], strokeWidth: 2.4 },
  'chevron-down': { paths: ['m6 9 6 6 6-6'], strokeWidth: 2.2 },
  'arrow-right': { paths: ['M5 12h14M12 5l7 7-7 7'], strokeWidth: 2.4 },
  'arrow-up-right': { paths: ['M7 17 17 7M17 7h-7M17 7v7'], strokeWidth: 2.6 },
  check: { paths: ['M20 6 9 17l-5-5'], strokeWidth: 2.4 },
  alert: {
    paths: ['M12 8v5M12 17h.01'],
    circles: [{ cx: 12, cy: 12, r: 9 }],
    strokeWidth: 2.2,
  },
  clock: {
    paths: ['M12 7v5l3 2'],
    circles: [{ cx: 12, cy: 12, r: 9 }],
    strokeWidth: 2.4,
  },
  star: {
    paths: ['m12 3 2.6 5.6 6.1.8-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.4l6.1-.8z'],
    strokeWidth: 2.1,
  },
  close: { paths: ['M18 6 6 18M6 6l12 12'], strokeWidth: 2.2 },
  logout: { paths: ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9'] },
});

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 17, strokeWidth, ...rest }: IconProps): JSX.Element {
  const shape = ICONS[name];

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth ?? shape.strokeWidth ?? 2}
      strokeLinecap="round"
      strokeLinejoin="round"
      // Decorative: every icon here is paired with a text label, or its
      // interactive parent carries an aria-label.
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {shape.rects?.map((r) => (
        <rect key={`${r.x}-${r.y}`} x={r.x} y={r.y} width={r.width} height={r.height} rx={r.rx} />
      ))}
      {shape.circles?.map((c) => (
        <circle key={`${c.cx}-${c.cy}-${c.r}`} cx={c.cx} cy={c.cy} r={c.r} />
      ))}
      {shape.paths.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}
